<template>
  <div class="flex flex-col">
    <div class="font-heading text-2xl font-bold">Add {{ showName }} Performance</div>
    <ErrorDisplay v-bind="ui.error" />
    <div class="font-label text-fg-secondary text-xs uppercase mt-2">Date:</div>
    <div
      class="font-sans text-base text-fg-link-primary hover:cursor-pointer"
      @click="ui.showDatePicker = true"
    >
      {{ date }}
    </div>
    <OnClickOutside @trigger="ui.showDatePicker = false">
      <div v-if="ui.showDatePicker" class="absolute z-10">
        <DatePicker :selectedDate="date" @datePickerChange="onDatePickerChange" />
      </div>
    </OnClickOutside>

    <div class="font-label text-fg-secondary text-xs uppercase mt-2">Floor Plan:</div>
    <ListBox
      :options="floorPlanStore.arrangements"
      :selected="arrangement"
      itemText="name"
      itemValue="id"
      @listBoxChange="setArrangement"
    />

    <div class="font-label text-fg-secondary text-xs uppercase mt-2">Showtime:</div>
    <ListBox
      :options="showtimeStore.showtimes"
      :selected="showtime"
      itemText="display"
      itemValue="id"
      @listBoxChange="setShowtime"
    />
    <ButtonDefault id="performance-form-save-btn" label="Save" class="mt-4" @click="onSave()" />
  </div>
</template>

<script lang="ts">
import { OnClickOutside } from '@vueuse/components'
import { mapStores } from 'pinia'
import { PropType, defineComponent } from 'vue'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import ListBox from '@/common/components/molecules/Listbox.vue'
import DatePicker from '@/common/components/organisms/DatePicker.vue'
import { StoreFloorPlan, StoreShow, StoreShowTime } from '@/models'
import { useFloorPlanStore } from '@/stores/floorplan'
import { useSettingStore } from '@/stores/setting'
import { useShowTimeStore } from '@/stores/showtime'
import { blankError, clearError, setError } from '@/utils/errors'
import { todayIsoStr } from '@/utils/utils'

import { PerformanceCreate } from '@generated/types'

export default defineComponent({
  name: 'PerformanceForm',
  components: {
    DatePicker,
    ErrorDisplay,
    OnClickOutside,
    ListBox,
    ButtonDefault,
  },
  props: {
    show: {
      type: Object as PropType<StoreShow>,
      required: true,
    },
  },
  emits: {
    // eslint-disable-next-line no-unused-vars
    save: (_perf: PerformanceCreate) => true,
  },
  data() {
    return {
      date: todayIsoStr(),
      arrangement: { name: 'loading...' } as StoreFloorPlan,
      showtime: { name: 'loading...' } as StoreShowTime,
      ui: {
        showDatePicker: false,
        error: blankError(),
      },
    }
  },
  async created() {
    await Promise.all([
      this.showtimeStore.fetchAll().then(() => {
        this.showtime = this.showtimeStore.showtimes[0] || { name: 'No showtimes present' }
      }),
      this.floorPlanStore.fetchAll().then(() => {
        this.arrangement =
          this.floorPlanStore.primaryFloorPlan || this.floorPlanStore.arrangements[0]
      }),
    ])
  },
  computed: {
    ...mapStores(useFloorPlanStore, useSettingStore, useShowTimeStore),
    showName(): string {
      if (!this.show) return ''
      return this.show.name || ''
    },
    performanceCreate(): PerformanceCreate {
      return {
        datetime: this.settingStore.makeDateTime({
          date: this.date,
          time: this.settingStore.time24Str(this.showtime.time),
        }),
        show_id: this.show.id,
        arrangement_id: this.arrangement.id,
        showtime_id: this.showtime.id,
      }
    },
  },
  methods: {
    setShowtime(showtime: StoreShowTime) {
      this.showtime = showtime
    },
    setArrangement(arrangement: StoreFloorPlan) {
      this.arrangement = arrangement
    },
    onSave() {
      clearError(this.ui.error)
      if (!this.showtime) {
        setError(this.ui.error, {
          code: 'showtime_required',
          message: 'You must choose a showtime',
        })
        return
      }
      if (!this.arrangement) {
        setError(this.ui.error, {
          code: 'arrangement_required',
          message: 'You must choose a arrangemetn',
        })
        return
      }
      this.$emit('save', this.performanceCreate)
    },
    onDatePickerChange(evt: { date: string }) {
      this.date = evt.date
      this.ui.showDatePicker = false
    },
  },
})
</script>
